import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import {
  conditionNames,
  propertyOperators,
  getFilteredClauseTree
} from "@appcues/libcues";
import {
  CCheckbox,
  Notice,
  NOTICE_TYPES,
  Caption,
  CLink,
  H4,
  Box
} from "@appcues/component-library";
import { filterDomainTargetingClauses } from "utils/conditions";
import { selectInstalledDomains } from "reducers/account/installedDomains";

import Radio from "components/Common/UI/Forms/Radio";
import DomainItem from "components/Common/DomainItem";
import DomainLegend from "components/Common/DomainLegend";
import "components/Common/DomainTargeting/style.scss";

class DomainTargeting extends Component {
  state = {
    forceShowDomains: "",
    error: false
  };

  componentDidMount() {
    if (this.state.forceShowDomains === "") {
      // set initial forceShowDomains based on the first domainClauses and installedDomains length we see in this session

      const domainClauses = this.getDomainClausesFromConditions();

      this.setState({
        forceShowDomains: domainClauses.length > 0
      });
    }
  }

  handleEverywhereClick = () => {
    const { deleteMatchingClauses, stepId } = this.props;

    deleteMatchingClauses(stepId, filterDomainTargetingClauses);
    this.setState({ forceShowDomains: false, error: false });
  };

  getDomainClausesFromConditions = () => {
    const { conditions } = this.props;

    const domainClauses = getFilteredClauseTree(
      filterDomainTargetingClauses,
      conditions ? conditions : []
    );

    return domainClauses.filter(
      clause => clause.conditionName === conditionNames.DOMAINS && clause.value
    );
  };

  getAllDomains = () => {
    const { meta } = this.props;
    const metaDomains = (meta.domains || "").split(",");
    const domainsFromClauses = this.getDomainClausesFromConditions().map(
      clause => clause.value
    );

    const allDomains = Array.from(
      new Set([...domainsFromClauses, ...metaDomains])
    );

    return allDomains;
  };

  getSortedDomainList = () => {
    const { installedDomains } = this.props;

    const allDomains = this.getAllDomains();

    const sortedByInstalled = allDomains
      .map(metaDomain => {
        return {
          metaDomain,
          isInstalled: installedDomains.includes(metaDomain)
        };
      })
      .sort((a, b) => {
        return a.metaDomain.localeCompare(b.metaDomain);
      })
      .sort((a, b) => {
        if (a.isInstalled === b.isInstalled) {
          return a.metaDomain.localeCompare(b.metaDomain);
        } else if (a.isInstalled) {
          return -1;
        }
        return 1;
      });

    return sortedByInstalled;
  };

  handleDomainsClick = () => {
    const { addClause, stepId } = this.props;

    const domainClauses = this.getDomainClausesFromConditions();

    const sortedByInstalled = this.getSortedDomainList();
    const parentId =
      domainClauses.length > 0 ? domainClauses[0].parentId : null;

    if (
      domainClauses.length === 0 &&
      sortedByInstalled &&
      sortedByInstalled[0]
    ) {
      addClause(stepId, parentId, {
        conditionName: conditionNames.DOMAINS,
        operator: propertyOperators.EQUALS,
        value: sortedByInstalled[0].metaDomain
      });
    }

    this.setState({ forceShowDomains: true });
  };

  handleDomainBoxClick = (
    domainObj,
    included,
    onlyOneDomain,
    domainClause,
    parentId
  ) => {
    const { addClause, deleteClause, stepId } = this.props;
    const { metaDomain } = domainObj;

    if (included && !onlyOneDomain) {
      this.setState({ error: false });
      deleteClause(stepId, domainClause.id, domainClause);
    } else if (included && onlyOneDomain) {
      this.setState({ error: true });
    } else {
      this.setState({ error: false });
      addClause(stepId, parentId, {
        conditionName: conditionNames.DOMAINS,
        operator: propertyOperators.EQUALS,
        value: metaDomain
      });
    }
  };

  render() {
    const { error } = this.state;

    const domainClauses = this.getDomainClausesFromConditions();

    const allDomains = this.getAllDomains();

    const showDomains = this.state.forceShowDomains;
    const onlyOneDomain = domainClauses.length === 1;
    return (
      <Box marginY={25}>
        <H4>On which environments?</H4>
        <Radio
          label="Everywhere my Appcues embed script is installed"
          onClick={this.handleEverywhereClick}
          checked={!showDomains}
        />
        <Radio
          label="Only the domains I choose"
          onClick={this.handleDomainsClick}
          checked={showDomains}
        />
        {error && (
          <Notice type={NOTICE_TYPES.warning}>
            Please leave at least one domain selected or select the option
            above.
          </Notice>
        )}
        {(() => {
          if (showDomains) {
            // if the user has already specified a set of domains in their conditions, latch-on
            const parentId =
              domainClauses.length > 0 ? domainClauses[0].parentId : null;
            const listLength = allDomains.length;
            const columnClass = listLength > 5 ? "content col2" : `content`;

            const sortedByInstalled = this.getSortedDomainList();

            return (
              <div className={columnClass}>
                <Caption>
                  Don't see the domain you're looking for?{" "}
                  <CLink to="/settings/account" is={Link}>
                    Update domain list
                  </CLink>
                </Caption>
                <Box marginTop={10}>
                  {sortedByInstalled.map((domainObj, i) => {
                    const { isInstalled, metaDomain } = domainObj;
                    const domainClause = domainClauses.find(
                      domain => domain.value === metaDomain
                    );
                    const included = !!domainClause;

                    return (
                      metaDomain && (
                        <div className="option" key={`${metaDomain}-${i}`}>
                          <CCheckbox
                            checked={included}
                            onChange={() =>
                              this.handleDomainBoxClick(
                                domainObj,
                                included,
                                onlyOneDomain,
                                domainClause,
                                parentId
                              )
                            }
                          />
                          <DomainItem
                            metaDomain={metaDomain}
                            isInstalled={isInstalled}
                          />
                        </div>
                      )
                    );
                  })}
                </Box>
              </div>
            );
          }
          return null;
        })()}
        {showDomains && <DomainLegend />}
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    installedDomains: selectInstalledDomains(state)
  };
}

export default connect(mapStateToProps)(DomainTargeting);
